import React, { useState } from 'react';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

export interface ChatTarget { block: string; cardNumber?: number | null }
export interface CreditFinding extends ChatTarget {
  id: string;
  category: 'attention' | 'suggestion';
  kind: 'placeholder' | 'empty_credit' | 'possible_duplicate' | 'formatting_difference';
  role?: string;
  setting?: string;
  evidence: string;
  referenceValue?: string;
  occurrences?: Array<ChatTarget & { role: string; row: number }>;
}
export interface CreditReview {
  findings: CreditFinding[];
  counts: { attention: number; suggestion: number };
  savedDataOnly: boolean;
}

// Shared by edit previews and review findings; resolve translations at render time.
export function settingLabel(key?: string): string {
  const labels: Record<string, string> = {
    block_name: t`Block name`, credit_role: t`Role`, credited_name: t`Credited name`,
    block_name_font_style: t`Block heading font`, block_name_font_size: t`Block heading size`,
    block_name_color: t`Block heading color`, block_name_alignment: t`Block heading alignment`,
    label_font_name: t`Role font`, label_font_size: t`Role font size`, label_color: t`Role color`, label_align: t`Role alignment`,
    value_font_name: t`Name font`, value_font_size: t`Name font size`, value_color: t`Name color`, value_align: t`Name alignment`,
    distance_between_rows: t`Distance between rows`, distance_from_previous: t`Distance from previous block`,
    label_value_gap: t`Role/name gap`, show_block_name: t`Display block name`, column_num: t`Number of columns`,
    mode: t`Video mode`, width: t`Width`, height: t`Height`, fps_num: t`Frame rate`,
  };
  return key ? labels[key] ?? key : t`Value`;
}

export function targetLabel(target: ChatTarget): string {
  return target.cardNumber ? t`${target.block} · Card ${target.cardNumber}` : target.block;
}

export function actionLabel(type: string): string {
  const labels: Record<string, string> = {
    block_created: t`Block added`, block_deleted: t`Block deleted`, value_update: t`Credit updated`, role_rename: t`Role renamed`,
    block_rename: t`Block renamed`, columns_changed: t`Columns changed`, entries_added: t`Credits added`,
  };
  return labels[type] ?? type.replace(/_/g, ' ');
}

export function readableValue(value: unknown): string {
  if (value === '' || value === undefined || value === null) return t`Empty`;
  if (value === true || value === 'true') return t`On`;
  if (value === false || value === 'false') return t`Off`;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (parsed && typeof parsed === 'object') {
      if (!Array.isArray(parsed)) {
        const name = parsed.family ?? parsed.fontFamily ?? parsed.name;
        if (typeof name === 'string') return name;
      }
      return JSON.stringify(parsed, null, 2);
    }
  } catch { /* Plain text is the common value shape. */ }
  return String(value);
}

export const PreviewValue: React.FC<{ value: unknown }> = ({ value }) => {
  const formatted = readableValue(value);
  const raw = typeof value === 'string' ? value : String(value ?? '');
  return <div className="hub-preview-value" dir="auto">
    {/^#[\da-f]{3,8}$/i.test(raw) && <span className="hub-color-swatch" style={{ backgroundColor: raw }} aria-hidden="true" />}
    <span>{formatted.length > 100 ? `${formatted.slice(0, 100)}…` : formatted}</span>
    {(formatted.length > 100 || (formatted !== raw && raw.startsWith('{'))) && <details><summary>{t`Full value`}</summary><pre>{raw}</pre></details>}
  </div>;
};

const PAGE_SIZE = 10;
export const ProductionHubReview: React.FC<{ review: CreditReview }> = ({ review }) => {
  useLingui();
  const [page, setPage] = useState(0);
  const ordered = [...review.findings.filter(f => f.category === 'attention'), ...review.findings.filter(f => f.category === 'suggestion')];
  const pages = Math.max(1, Math.ceil(ordered.length / PAGE_SIZE));
  const explanations = {
    placeholder: t`A placeholder is still present. Check whether it is intentional template content.`,
    empty_credit: t`This role has no credited value.`,
    possible_duplicate: t`This credited value appears more than once. Repetition may be intentional.`,
    formatting_difference: t`This setting differs from most comparable blocks. The difference may be intentional.`,
  };
  return <section className="hub-credit-review" aria-label={t`Credit review`}>
    <h4>{t`Check my credits`}</h4>
    <p className="hub-review-note">{t`Checked saved project data. Save your changes and rerun after editing. This review does not inspect the rendered layout.`}</p>
    <p>{t`Needs attention`}: {review.counts.attention} · {t`Suggestions`}: {review.counts.suggestion}</p>
    {!ordered.length && <p>{t`No issues found by these checks.`}</p>}
    {ordered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map(finding => <article key={finding.id} className="hub-finding">
      <strong>{finding.category === 'attention' ? t`Needs attention` : t`Suggestion`} · <bdi>{targetLabel(finding)}</bdi></strong>
      <p>{explanations[finding.kind]}</p>
      <p><bdi>{finding.role || settingLabel(finding.setting)}</bdi></p>
      <PreviewValue value={finding.evidence} />
      {finding.referenceValue !== undefined && <div>{t`Most comparable blocks`}: <PreviewValue value={finding.referenceValue} /></div>}
      {finding.occurrences && <details><summary>{t`Show occurrences`} ({finding.occurrences.length})</summary><ul>{finding.occurrences.map((entry, index) => <li key={index}><bdi>{targetLabel(entry)} · {entry.role}</bdi></li>)}</ul></details>}
    </article>)}
    {pages > 1 && <nav className="hub-review-pagination" aria-label={t`Credit review pages`}>
      <button type="button" disabled={page === 0} onClick={() => setPage(p => p - 1)}>{t`Previous`}</button>
      <span>{t`Page ${page + 1} of ${pages}`}</span>
      <button type="button" disabled={page + 1 === pages} onClick={() => setPage(p => p + 1)}>{t`Next`}</button>
    </nav>}
    {!!ordered.length && <p className="hub-review-note">{t`Ask me to fix a specific finding. You will review the proposed change before confirming.`}</p>}
  </section>;
};
